import React, { useState } from 'react';
import { Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { mobilizeiAppPublicationsService } from '../_services/mobilizei/appPublications.service';

const useStyles = makeStyles(() => ({
    field: { marginBottom: 12 },
    error: { color: '#c62828', marginTop: 8 },
    success: { color: '#43a047', fontWeight: 'bold' },
}));

export default function IosCredentialsStep({ store, setLocalStore, platformId, isFinished }) {
    const classes = useStyles();

    const [phase, setPhase] = useState('auth');
    const [appleId, setAppleId] = useState('apps@mobilizei.com.br');
    const [password, setPassword] = useState('');
    const [authRef, setAuthRef] = useState(null);
    const [phones, setPhones] = useState([]);
    const [phoneId, setPhoneId] = useState('');
    const [code, setCode] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const showError = (e) => {
        const fields = e?.missingFields ? ` (campos: ${e.missingFields.join(', ')})` : '';
        setError(`${e?.message || 'Erro inesperado'}${fields}`);
    };

    const handleAuth = () => {
        setLoading(true);
        setError(null);
        mobilizeiAppPublicationsService
            .iosCredentialsAuthStart({ storeId: store.id, platformId: platformId || store.platformId, appleId, password })
            .then((res) => {
                setAuthRef(res.authRef);
                if (res.status === 'authenticated') {
                    setError('A conta não exigiu 2FA — fluxo não suportado. Acione o desenvolvedor.');
                    return;
                }
                setPhones(res.phones || []);
                if (res.phones?.length) setPhoneId(res.phones[0].id);
                setPhase('phone');
            })
            .catch(showError)
            .finally(() => setLoading(false));
    };

    const handleSendSms = () => {
        setLoading(true);
        setError(null);
        mobilizeiAppPublicationsService
            .iosCredentialsSendSms({ authRef, phoneId })
            .then(() => setPhase('code'))
            .catch(showError)
            .finally(() => setLoading(false));
    };

    const handleVerify = () => {
        setLoading(true);
        setError(null);
        mobilizeiAppPublicationsService
            .iosCredentialsVerify({ storeId: store.id, platformId: platformId || store.platformId, authRef, code })
            .then((res) => {
                setResult(res);
                setPhase('done');
                setLocalStore(prev => ({
                    ...prev,
                    platforms: {
                        ...prev.platforms,
                        ios: {
                            ...(prev.platforms?.ios || {}),
                            ascApiKeyId: res.ascApiKeyId,
                            distributionCertificate: res.distributionCertificate || prev.platforms?.ios?.distributionCertificate,
                        },
                    },
                }));
                isFinished(true);
            })
            .catch(showError)
            .finally(() => setLoading(false));
    };

    return (
        <Box>
            <Typography variant="body2" style={{ marginBottom: 16 }}>
                Para gerar builds iOS, é necessário configurar as credenciais da conta Apple.
                Isso registra a chave de envio (ASC API Key), o certificado de distribuição e os
                perfis de provisionamento automaticamente.
            </Typography>

            {phase === 'auth' && (
                <>
                    <FormControl fullWidth className={classes.field}>
                        <InputLabel shrink>Apple ID</InputLabel>
                        <TextField variant="standard" value={appleId} onChange={(e) => setAppleId(e.target.value)} />
                    </FormControl>
                    <FormControl fullWidth className={classes.field}>
                        <InputLabel shrink>Senha</InputLabel>
                        <TextField type="password" variant="standard" value={password} onChange={(e) => setPassword(e.target.value)} />
                    </FormControl>
                </>
            )}

            {phase === 'phone' && (
                <>
                    <Typography variant="body2" style={{ marginBottom: 12 }}>
                        Escolha o número para receber o código de verificação por SMS.
                    </Typography>
                    <FormControl fullWidth className={classes.field}>
                        <InputLabel shrink>Telefone</InputLabel>
                        <Select
                            value={phoneId}
                            onChange={(e) => setPhoneId(e.target.value)}
                            MenuProps={{ disablePortal: true }}
                        >
                            {phones.map((p) => (
                                <MenuItem key={p.id} value={p.id}>{p.number}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </>
            )}

            {phase === 'code' && (
                <>
                    <Typography variant="body2" style={{ marginBottom: 12 }}>
                        Digite o código de 6 dígitos recebido por SMS.
                    </Typography>
                    <FormControl fullWidth className={classes.field}>
                        <InputLabel shrink>Código</InputLabel>
                        <TextField variant="standard" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" />
                    </FormControl>
                </>
            )}

            {phase === 'done' && (
                <Typography variant="body2" className={classes.success}>
                    ✅ Credenciais iOS configuradas com sucesso (certificado + perfis de build + chave de envio).
                    Clique em <strong>Próximo</strong> para continuar.
                </Typography>
            )}

            {error && <Typography variant="body2" className={classes.error}>{error}</Typography>}

            {phase !== 'done' && (
                <Box mt={2}>
                    {loading ? (
                        <CircularProgress size={26} />
                    ) : (
                        <>
                            {phase === 'auth' && (
                                <Button variant="contained" color="primary" disabled={!appleId || !password} onClick={handleAuth}>
                                    Autenticar
                                </Button>
                            )}
                            {phase === 'phone' && (
                                <Button variant="contained" color="primary" disabled={!phoneId} onClick={handleSendSms}>
                                    Enviar SMS
                                </Button>
                            )}
                            {phase === 'code' && (
                                <Button variant="contained" color="primary" disabled={!code} onClick={handleVerify}>
                                    Verificar e configurar
                                </Button>
                            )}
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
}
